import React from 'react';
import i18n from 'i18n';
import Button from 'components/Button';
import {Grid} from '@mui/material';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {WarningButton} from 'components/buttons/WarningButton';
import makeStyles from '@mui/styles/makeStyles';
import {isEmpty} from 'lodash';
import {loginSelectors} from 'features/login/redux';
import {useSelector} from 'react-redux';
import {useToasts} from 'features/notifications/useToasts';
import {combineYupErrors} from 'common/yup/combineYupErrors';
import {useFormContext} from 'react-hook-form';
import {useIsPermitted} from 'common/hooks/useIsPermitted';
import {DiplomatObjectAceType} from 'types/diplomat/settings/permissions/DiplomatObjectAceType';
import {LoaderInline} from 'components/Loader';

type Props = {
   cancel?: () => void;
   submitLabel?: React.ReactNode;
   cancelLabel?: string;
   resetLabel?: string;
   resettable?: boolean;
   enableOk?: boolean;
   loading?: boolean;
   skipPermissionCheck?: boolean;
};

export const useFormFooterStyles = makeStyles((theme) => ({
   root: {
      padding: theme.spacing(3, 0, 2, 0),
   },
   expandButton: {
      color: theme.palette.primary.main,
   },
}));

export const FormFooterButtons = (props: Props) => {
   const classes = useFormFooterStyles();
   const {
      cancel,
      submitLabel = i18n.t('Ok'),
      cancelLabel = i18n.t('Cancel'),
      resetLabel = i18n.t('Reset'),
      resettable = false,
      enableOk = true,
      loading,
      skipPermissionCheck,
   } = props;

   const hookFormContext = useFormContext();

   const id = hookFormContext.watch('id');
   const objectType = hookFormContext.watch('objectType');
   const isPermitted = useIsPermitted(skipPermissionCheck ? null : objectType, id);

   const isValid = hookFormContext.formState.isValid;
   const dirty = hookFormContext.formState.isDirty;
   const touched = !isEmpty(hookFormContext.formState.touchedFields);
   const isSubmitting = hookFormContext.formState.isSubmitting;
   const errors = hookFormContext.formState.errors;
   const setTouched = (vals: any) => console.debug('touch', vals);
   const resetForm = hookFormContext.reset;

   const cancelable = Boolean(cancel);

   const isReviewer = useSelector(loginSelectors.isReviewer);
   const {addToast} = useToasts();

   // console.debug(touched, hookFormContext.formState.touchedFields);
   // console.debug('Form', enableOk, isSubmitting, dirty, touched);
   return (
      <Grid className={classes.root} container justifyContent="center" spacing={3}>
         {!isReviewer &&
            (hookFormContext ? (
               isPermitted(DiplomatObjectAceType.enum.MODIFY, true) && (
                  <>
                     <Grid item>
                        <Button
                           key="validOk"
                           type="submit"
                           color={isValid ? 'primary' : 'warning'}
                           disabled={loading || !enableOk || isSubmitting || (!dirty && !touched)}
                        >
                           {loading ? <LoaderInline /> : submitLabel}
                        </Button>
                     </Grid>
                  </>
               )
            ) : (
               <>
                  {isValid ? (
                     <Grid item>
                        <PrimaryButton
                           key="validOk"
                           type="submit"
                           disabled={!enableOk || isSubmitting || (!dirty && !touched)}
                        >
                           {submitLabel}
                        </PrimaryButton>
                     </Grid>
                  ) : (
                     <Grid item>
                        <WarningButton
                           key="invalidOk"
                           type="button"
                           disabled={isSubmitting}
                           onClick={() => {
                              console.warn('Form errors', errors);
                              console.warn('Form context', hookFormContext);

                              // TODO if the error field is not registered, but exist in schemas, the error will not appear here...
                              // const a = LdapSettings.validate(hookFormContext.getValues());
                              // console.warn('Form a', a);

                              setTouched(errors as any);
                              const errorList = [] as string[];
                              combineYupErrors(errorList, errors);
                              addToast(
                                 `Form is not valid. \n${errorList.join(
                                    '\n'
                                 )} \nIf you have any difficulties please contact our support.`,
                                 {appearance: 'warning'}
                              );
                           }}
                        >
                           {submitLabel}
                        </WarningButton>
                     </Grid>
                  )}
               </>
            ))}
         {cancelable ? (
            <Grid item>
               <Button type="button" onClick={() => cancel?.()}>
                  {cancelLabel}
               </Button>
            </Grid>
         ) : null}
         {resettable ? (
            <Grid item>
               <Button
                  type="reset"
                  disabled={isSubmitting || (!dirty && !touched)}
                  onClick={() => resetForm()}
               >
                  {resetLabel}
               </Button>
            </Grid>
         ) : null}
      </Grid>
   );
};
